import { TeamName } from "../../../../Components/TeamName";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { FC, useEffect, useState } from "react";
import {
  Button,
  Group,
  Loader,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { DateTime, Duration } from "luxon";
import {
  livestreamRegieReady,
  postponeGame,
} from "../../../../openapi/api-client";
import { useMutation } from "@tanstack/react-query";
import { useGame } from "../../../../hooks/useGame.ts";
import { useCompetition } from "../../../../hooks/useCompetition.ts";

interface RegieGameDetailsProps {
  gameId: string;
}

export const RegieGameDetails: FC<RegieGameDetailsProps> = (props) => {
  const { gameId } = props;

  const regieReadyMutation = useMutation({ mutationFn: livestreamRegieReady });
  const onClickRegieReady = () => {
    void regieReadyMutation.mutateAsync({
      id: gameId,
    });
  };

  const gameData = useGame(gameId);
  const competition = useCompetition(gameData.data?.competitionId);

  const matchday = competition.data?.matchdays.find(
    (md) => md.id === gameData.data?.matchdayId,
  );

  const postponeGameMutation = useMutation({ mutationFn: postponeGame });
  const onClickPostpone = async (minutes: number) => {
    await postponeGameMutation.mutateAsync({
      id: gameId,
      postponedToDateTime: DateTime.now()
        .plus(Duration.fromObject({ minutes }))
        .toISO(),
    });
  };

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  if (!gameData.isSuccess) {
    return <Loader />;
  }

  const game = gameData.data;

  return (
    <Paper style={{ textAlign: "center" }} p="lg" shadow="sm">
      <Title order={2}>{game.name}</Title>
      <Stack>
        <div>{matchday ? matchday.name : <Skeleton>Matchday</Skeleton>}</div>
        {game.actualTeams.homeTeamId && game.actualTeams.guestTeamId ? (
          <Group justify="center">
            <div>
              <Text fw={700} fz="xl" ta="right">
                <TeamName
                  variant={"medium"}
                  teamId={game.actualTeams.homeTeamId}
                />
              </Text>
            </div>
            <div>
              <TeamLogo teamId={game.actualTeams.homeTeamId} height={50} />
            </div>
            <div>
              <TeamLogo teamId={game.actualTeams.guestTeamId} height={50} />
            </div>
            <div>
              <Text fw={700} fz="xl" ta="left">
                <TeamName
                  variant={"medium"}
                  teamId={game.actualTeams.guestTeamId}
                />
              </Text>
            </div>
          </Group>
        ) : (
          <Text fw={700} fz="xl">
            Teams stehen noch nicht fest
          </Text>
        )}
        <div>
          <Button
            onClick={onClickRegieReady}
            size="xl"
            disabled={game.livestreamRegieReady}
            color="green"
          >
            Regie bereit
          </Button>
        </div>
        <Text>
          Spielplan: {DateTime.fromISO(game.dateTime).toFormat("HH:mm")} Uhr
        </Text>
        <Text size="xl" fw={700}>
          {game.livestreamRegieReady ? (
            <>&nbsp;</>
          ) : (
            DateTime.fromISO(game.postponedDateTime)
              .diff(DateTime.fromJSDate(currentTime))
              .toFormat("mm:ss")
          )}
        </Text>

        <Group justify="center">
          <Button
            onClick={() => void onClickPostpone(1)}
            disabled={game.livestreamRegieReady}
          >
            1 Min.
          </Button>
          <Button
            onClick={() => void onClickPostpone(3)}
            disabled={game.livestreamRegieReady}
          >
            3 Min.
          </Button>
          <Button
            onClick={() => void onClickPostpone(5)}
            disabled={game.livestreamRegieReady}
          >
            5 Min.
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
